import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Book, 
  Calendar, 
  Home, 
  Users, 
  FileText, 
  Settings, 
  ArrowLeftRight,
  RotateCw,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Search,
  Plane,
  Clock,
  Shield,
  CheckCircle,
  AlertCircle,
  Bug,
  Lightbulb,
  Send,
  MessageSquare,
  Eye,
  Upload,
  X,
  Image,
  Monitor,
  Cake
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HelpPage = () => {
  const { user, token } = useAuth();
  const [expandedSection, setExpandedSection] = useState('getting-started');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showMyFeedback, setShowMyFeedback] = useState(false);
  const [myFeedback, setMyFeedback] = useState([]);
  const [feedbackForm, setFeedbackForm] = useState({
    type: '',
    title: '',
    description: '',
    priority: 'medium'
  });
  const [submitting, setSubmitting] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  // Collect system info for debugging
  const getSystemInfo = () => {
    const nav = navigator;
    return {
      userAgent: nav.userAgent,
      platform: nav.platform,
      language: nav.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      colorDepth: window.screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookiesEnabled: nav.cookieEnabled,
      onLine: nav.onLine,
      timestamp: new Date().toISOString()
    };
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Screenshot must be less than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setScreenshotPreview(event.target.result);
        setScreenshot({
          data: event.target.result,
          filename: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const fetchMyFeedback = async () => {
    try {
      const response = await axios.get(`${API}/feedback`, { headers });
      setMyFeedback(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  useEffect(() => {
    if (showMyFeedback) {
      fetchMyFeedback();
    }
  }, [showMyFeedback]);

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!feedbackForm.type) errors.type = 'Please select a type (Bug or Enhancement)';
    if (!feedbackForm.title.trim()) errors.title = 'Title is required';
    if (!feedbackForm.description.trim()) errors.description = 'Description is required';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fill in all required fields');
      return;
    }
    setFormErrors({});

    setSubmitting(true);
    try {
      const payload = {
        ...feedbackForm,
        system_info: getSystemInfo(),
        current_page: window.location.href,
        screenshot: screenshot?.data || null,
        screenshot_filename: screenshot?.filename || null
      };
      
      await axios.post(`${API}/feedback`, payload, { headers });
      toast.success('Thank you! Your feedback has been submitted.');
      setFeedbackForm({ type: '', title: '', description: '', priority: 'medium' });
      setFormErrors({});
      setScreenshot(null);
      setScreenshotPreview(null);
      setShowFeedbackForm(false);
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
      console.error('Error submitting feedback:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      wont_do: 'bg-red-100 text-red-800'
    };
    const labels = {
      new: 'New',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed',
      wont_do: "Won't Do"
    };
    return <Badge className={styles[status] || styles.new}>{labels[status] || status}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (type === 'bug') {
      return <Badge className="bg-red-100 text-red-800"><Bug className="h-3 w-3 mr-1" />Bug</Badge>;
    }
    return <Badge className="bg-purple-100 text-purple-800"><Lightbulb className="h-3 w-3 mr-1" />Enhancement</Badge>;
  };

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Book,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Logging In</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Navigate to the Shiftela application URL</li>
            <li>Enter your email address and password</li>
            <li>Click <strong>Sign In</strong></li>
          </ol>
          
          <h3 className="font-semibold text-lg mt-6">User Roles</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Permissions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2 font-medium">Employee</td><td className="px-4 py-2 text-gray-600">View own schedule, request WFH/PTO, swap shifts</td></tr>
                <tr><td className="px-4 py-2 font-medium">Team Lead</td><td className="px-4 py-2 text-gray-600">Employee permissions + view team schedules</td></tr>
                <tr><td className="px-4 py-2 font-medium">Manager</td><td className="px-4 py-2 text-gray-600">Team Lead permissions + approve requests, create schedules</td></tr>
                <tr><td className="px-4 py-2 font-medium">Admin</td><td className="px-4 py-2 text-gray-600">Full access to all features including user management</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 'schedule',
      title: 'Schedule Management',
      icon: Calendar,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Viewing Schedules</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Click <strong>Schedule</strong> in the navigation menu</li>
            <li>Use the <strong>arrow buttons</strong> to navigate between weeks</li>
            <li>Click <strong>Today</strong> to return to the current week</li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Creating a Shift</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Click the <strong>+ Add Shift</strong> button or click on an empty cell</li>
            <li>Fill in the shift details:
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Employee</strong> - Select the employee</li>
                <li><strong>Date</strong> - Choose the date</li>
                <li><strong>Start/End Time</strong> - Set shift times</li>
                <li><strong>Shift Type</strong> - Regular, On-Call, PTO, etc.</li>
                <li><strong>Work Location</strong> - Office or Remote</li>
              </ul>
            </li>
            <li>Click <strong>Save</strong></li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Copying Schedules (Week Copy)</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Click <strong>Copy Week</strong> button</li>
            <li>Select <strong>Copy Mode</strong>:
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Copy Entire Week</strong> - Copies all shifts</li>
                <li><strong>Copy Specific Employees</strong> - Select which employees</li>
                <li><strong>Copy Specific Days</strong> - Select which days</li>
              </ul>
            </li>
            <li>Set <strong>Source Week</strong> and <strong>Target Week</strong></li>
            <li>(Optional) Check <strong>Overwrite existing schedules</strong></li>
            <li>Click <strong>Copy Schedules</strong></li>
          </ol>
          
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
            <p className="text-blue-700"><strong>Note:</strong> Remote/Office status and all shift details are preserved when copying.</p>
          </div>
        </div>
      )
    },
    {
      id: 'wfh',
      title: 'Work From Home (WFH)',
      icon: Home,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">WFH Types</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Uses Flex Bank?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-2 font-medium text-blue-600">Flex Bank</td>
                  <td className="px-4 py-2 text-gray-600">Standard WFH using your allocated days</td>
                  <td className="px-4 py-2"><span className="text-amber-600 font-medium">Yes</span></td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium text-orange-600">On-Call</td>
                  <td className="px-4 py-2 text-gray-600">WFH while on-call duty</td>
                  <td className="px-4 py-2"><span className="text-green-600 font-medium">No</span></td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium text-purple-600">Business Directed</td>
                  <td className="px-4 py-2 text-gray-600">Company-mandated WFH (weather, office closure)</td>
                  <td className="px-4 py-2"><span className="text-green-600 font-medium">No</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="font-semibold text-lg mt-6">Submitting a WFH Request</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Click <strong>Request WFH</strong> button on the Schedule page</li>
            <li><strong>Select WFH Type</strong> (Required) - Choose Flex Bank or On-Call</li>
            <li>Select the shifts you want to work from home</li>
            <li>Choose <strong>Hours</strong>:
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Full Day (8 hours)</strong> - Uses 1 Flex Bank day</li>
                <li><strong>Half Day (4 hours)</strong> - Uses 0.5 Flex Bank days</li>
              </ul>
            </li>
            <li>(Optional) Add a reason</li>
            <li>Click <strong>Submit Request</strong></li>
          </ol>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4">
            <p className="text-amber-700"><strong>Important:</strong> WFH Type selection is mandatory. You must select a type before submitting.</p>
          </div>

          <h3 className="font-semibold text-lg mt-6">Historical WFH Entry</h3>
          <p className="text-gray-700">To record past WFH days (e.g., unplanned sick child):</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 mt-2">
            <li>Click <strong>Request WFH</strong></li>
            <li>Check the <strong>Historical Entry</strong> checkbox</li>
            <li>Select the date(s)</li>
            <li>Choose WFH type and hours</li>
            <li>Submit - Historical entries are auto-approved</li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Flex Bank Balance</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Each employee starts with <strong>15 Flex Bank days</strong> per year</li>
            <li>Balance resets annually</li>
            <li>Check your balance in the WFH request modal or Reports page</li>
            <li><strong>Remote employees</strong> do not use Flex Bank</li>
          </ul>
        </div>
      )
    },
    {
      id: 'pto',
      title: 'PTO Management',
      icon: Plane,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Requesting PTO</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Navigate to <strong>PTO/WFH</strong> page</li>
            <li>Click <strong>Request PTO</strong></li>
            <li>Fill in:
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>PTO Type</strong> - Vacation, Sick, Personal, etc.</li>
                <li><strong>Start Date</strong> and <strong>End Date</strong></li>
                <li><strong>Exclude Weekends</strong> (optional)</li>
                <li><strong>Partial Day</strong> (optional) - For half-day requests</li>
                <li><strong>Reason</strong> (optional)</li>
              </ul>
            </li>
            <li>Click <strong>Submit</strong></li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">PTO Types</h3>
          <div className="flex flex-wrap gap-2">
            {['Vacation', 'Sick Leave', 'Personal Day', 'Bereavement', 'Jury Duty', 'Other'].map(type => (
              <span key={type} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">{type}</span>
            ))}
          </div>

          <h3 className="font-semibold text-lg mt-6">Approving/Denying PTO (Managers/Admins)</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Go to <strong>PTO/WFH</strong> page</li>
            <li>View requests in the <strong>Pending</strong> tab</li>
            <li>Click <strong>Approve</strong> or <strong>Deny</strong></li>
            <li>Approved PTO automatically creates a PTO shift on the schedule</li>
          </ol>
        </div>
      )
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: FileText,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Available Reports</h3>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">WFH / Flex Bank Report</h4>
              <ul className="list-disc list-inside mt-2 text-gray-600 space-y-1">
                <li>View all WFH requests by employee</li>
                <li>See Flex Bank usage statistics</li>
                <li>Track days used, remaining balance, pending requests</li>
                <li>Filter by date range and status</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">PTO Report</h4>
              <ul className="list-disc list-inside mt-2 text-gray-600 space-y-1">
                <li>View PTO requests and balances</li>
                <li>Track usage by PTO type</li>
                <li>See upcoming scheduled time off</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Flex Bank Audit (Admins)</h4>
              <ul className="list-disc list-inside mt-2 text-gray-600 space-y-1">
                <li>Click <strong>Audit</strong> next to an employee's Flex Bank info</li>
                <li>See all transactions: approvals, denials, manual adjustments</li>
                <li>Track who made changes and when</li>
              </ul>
            </div>
          </div>

          <h3 className="font-semibold text-lg mt-6">Exporting Reports</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Click the <strong>Export CSV</strong> button on any report</li>
            <li>Choose the report type</li>
            <li>The CSV file will download with all report data</li>
          </ol>
        </div>
      )
    },
    {
      id: 'admin',
      title: 'Admin Features',
      icon: Settings,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">User Management</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Navigate to <strong>Admin</strong> page</li>
            <li>Click <strong>Manage Employees</strong></li>
          </ol>

          <h4 className="font-medium mt-4">Adding a New User</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Click <strong>Add Employee</strong></li>
            <li>Fill in user details: Name, Email, Username, Role, Work Location</li>
            <li>Click <strong>Create User</strong></li>
          </ol>

          <h4 className="font-medium mt-4">Work Location Settings</h4>
          <div className="overflow-x-auto mt-2">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Setting</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-2 font-medium">Office</td>
                  <td className="px-4 py-2 text-gray-600">Default. Employee uses Flex Bank for WFH requests</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium">Remote</td>
                  <td className="px-4 py-2 text-gray-600">Permanent remote worker. No Flex Bank needed</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="font-semibold text-lg mt-6">Manual Balance Adjustment</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Go to <strong>Reports</strong> page</li>
            <li>Find the employee in the WFH report</li>
            <li>Click <strong>Audit</strong></li>
            <li>Click <strong>Adjust Balance</strong></li>
            <li>Enter amount (positive to add, negative to subtract) and reason</li>
            <li>Click <strong>Apply Adjustment</strong></li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Employee Birthday & Hire Date</h3>
          <p className="text-gray-700">To set an employee's birthday and hire date:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Go to <strong>Admin</strong> &gt; find the employee</li>
            <li>Click <strong>Edit</strong></li>
            <li>Set the <strong>Birthday</strong> and <strong>Hire Date</strong> fields (below Job Title)</li>
            <li>Click <strong>Save</strong></li>
          </ol>
          <p className="text-gray-700 mt-2">These dates are used by the Dashboard Celebrations feature to highlight birthdays and work anniversaries.</p>

          <h3 className="font-semibold text-lg mt-6">Feature Toggles</h3>
          <p className="text-gray-700">Optional features can be enabled or disabled for your organization:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Go to <strong>Admin</strong> &gt; <strong>Settings</strong> tab</li>
            <li>Find the <strong>Feature Toggles</strong> card</li>
            <li>Toggle features on or off as needed</li>
          </ol>
          <p className="text-gray-700 mt-2">Currently available toggles:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
            <li><strong>Dashboard Celebrations</strong> - Show/hide the birthday and work anniversary section on the Dashboard</li>
          </ul>

          <h3 className="font-semibold text-lg mt-6">Staffing Rules</h3>
          <p className="text-gray-700">Configure your organization's staffing requirements:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Go to <strong>Staffing Rules</strong> in the sidebar</li>
            <li>Set <strong>Operating Days</strong> and <strong>Business Hours</strong></li>
            <li>Define <strong>Time Block Rules</strong> with minimum staff counts per role for both regular and on-call shifts</li>
            <li>Use <strong>Coverage Comparison</strong> to see how your current schedule compares to the rules</li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Sidebar Access Control</h3>
          <p className="text-gray-700">Control which pages each user can see in the sidebar:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Edit a user in <strong>Admin</strong></li>
            <li>Scroll to the <strong>Sidebar Page Access</strong> section</li>
            <li>Select which pages the user can access, or choose <strong>Full Access</strong> for all pages</li>
          </ol>
        </div>
      )
    },
    {
      id: 'swaps',
      title: 'Shift Swaps',
      icon: ArrowLeftRight,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Requesting a Swap</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Click on your shift</li>
            <li>Select <strong>Request Swap</strong></li>
            <li>Choose the employee you want to swap with</li>
            <li>Select their shift to swap</li>
            <li>Add a reason (optional)</li>
            <li>Click <strong>Submit Swap Request</strong></li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Responding to Swap Requests</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>You'll receive a notification for swap requests</li>
            <li>Go to <strong>Shift Swaps</strong> page</li>
            <li>Review the request details</li>
            <li>Click <strong>Accept</strong> or <strong>Decline</strong></li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Manager Approval</h3>
          <p className="text-gray-700">After both parties agree:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 mt-2">
            <li>Manager reviews the swap</li>
            <li>Approves or denies the final swap</li>
            <li>If approved, shifts are automatically exchanged</li>
          </ol>
        </div>
      )
    },
    {
      id: 'rotations',
      title: 'Rotations',
      icon: RotateCw,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Understanding Rotations</h3>
          <p className="text-gray-700">Rotations allow you to create repeating schedule patterns that automatically generate shifts.</p>

          <h3 className="font-semibold text-lg mt-6">Creating a Rotation</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Go to <strong>Schedule</strong> page</li>
            <li>Click <strong>Rotations</strong></li>
            <li>Click <strong>Create Rotation</strong></li>
            <li>Set up the pattern:
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Name</strong> - Give the rotation a name</li>
                <li><strong>Employees</strong> - Who's in the rotation</li>
                <li><strong>Pattern</strong> - Define the weekly shift pattern</li>
                <li><strong>Start Date</strong> - When the rotation begins</li>
              </ul>
            </li>
            <li>Click <strong>Save Rotation</strong></li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Applying a Rotation</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Select the rotation</li>
            <li>Click <strong>Apply</strong></li>
            <li>Choose how many weeks to generate</li>
            <li>Click <strong>Generate Schedules</strong></li>
          </ol>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
            <p className="text-blue-700"><strong>Note:</strong> Generated shifts can be edited individually after creation.</p>
          </div>
        </div>
      )
    },
    {
      id: 'schedule-insights',
      title: 'Schedule Insights',
      icon: Eye,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Overview</h3>
          <p className="text-gray-700">Schedule Insights analyzes your workforce schedules to proactively identify issues, gaps, and potential conflicts. Access it from the <strong>Schedule Insights</strong> link in the sidebar.</p>

          <h3 className="font-semibold text-lg mt-6">Alert Summary Cards</h3>
          <p className="text-gray-700">At the top of the page, color-coded cards highlight key issues at a glance:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
            <li><strong>Expiring Schedules</strong> - Employees whose schedules end within the selected threshold</li>
            <li><strong>Unscheduled Employees</strong> - Active employees without any schedules assigned</li>
            <li><strong>Gap Days</strong> - Dates with fewer staff than normal based on historical baselines</li>
            <li><strong>Scheduling Conflicts</strong> - Duplicate or overlapping shifts that need attention</li>
            <li><strong>Excessive Hours</strong> - Employees scheduled significantly above their normal weekly hours</li>
          </ul>

          <h3 className="font-semibold text-lg mt-6">Normal Staffing Levels (Baselines)</h3>
          <p className="text-gray-700">The system calculates a 4-week historical average of staffing levels per role. This baseline is used to detect shortages and flag when coverage falls below normal.</p>

          <h3 className="font-semibold text-lg mt-6">Individual Schedule Gaps</h3>
          <p className="text-gray-700">Flags employees who are missing shifts from their normal pattern, grouped by role. Planned PTO is shown separately as <strong>shrinkage</strong> (amber) to distinguish it from true missing shifts (purple).</p>

          <h3 className="font-semibold text-lg mt-6">Scheduling Conflicts (Duplicate/Overlapping Shifts)</h3>
          <p className="text-gray-700">This section flags shifts that are likely mistakes or need review. The detection rules are:</p>
          <div className="overflow-x-auto mt-2">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scenario</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Flagged?</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-2 font-medium">Two Regular shifts same day</td>
                  <td className="px-4 py-2 text-red-600 font-semibold">Yes</td>
                  <td className="px-4 py-2 text-gray-600">Likely a scheduling error</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium">Any shifts with time overlap</td>
                  <td className="px-4 py-2 text-red-600 font-semibold">Yes</td>
                  <td className="px-4 py-2 text-gray-600">Conflicting schedules regardless of type</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium">Regular + On-Call (no overlap)</td>
                  <td className="px-4 py-2 text-green-600 font-semibold">No</td>
                  <td className="px-4 py-2 text-gray-600">Normal scheduling pattern</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium">Multiple On-Call shifts (no overlap)</td>
                  <td className="px-4 py-2 text-green-600 font-semibold">No</td>
                  <td className="px-4 py-2 text-gray-600">Split on-call coverage is expected</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 className="font-medium mt-4">Resolving Conflicts</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Click on any flagged conflict row to open the resolution dialog</li>
            <li>Review the conflicting shifts with their times and types</li>
            <li>For each shift you can:
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li>Click the <strong>pencil icon</strong> to edit the shift type or times</li>
                <li>Click the <strong>trash icon</strong> to delete the shift</li>
              </ul>
            </li>
            <li>After making changes, the insights page will automatically refresh</li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Excessive Hours</h3>
          <p className="text-gray-700">Compares each employee's scheduled regular hours against their historical average. Flags weeks where an employee is scheduled more than 10% above their normal hours. Results are grouped by role with the largest overages shown first.</p>

          <h3 className="font-semibold text-lg mt-6">Daily Staffing Levels Chart</h3>
          <p className="text-gray-700">A visual chart showing day-by-day staffing coverage across all roles, helping identify understaffed days at a glance.</p>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
            <p className="text-blue-700"><strong>Performance:</strong> Insights data is cached for 5 minutes. Click <strong>Refresh</strong> to force a fresh calculation. When you return to the page, previously loaded data is shown instantly while new data loads in the background.</p>
          </div>
        </div>
      )
    },
    {
      id: 'audit-log',
      title: 'Audit Log',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Overview</h3>
          <p className="text-gray-700">The Audit Log tracks <strong>every change</strong> made across the entire system, providing complete visibility into who changed what and when. Access it from the <strong>Audit Log</strong> link in the sidebar.</p>

          <h3 className="font-semibold text-lg mt-6">What's Tracked</h3>
          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
            <li><strong>User Management</strong> - Account creation, role changes, status toggles, profile updates, password resets, deletions</li>
            <li><strong>Schedule Changes</strong> - Shift creation, edits, deletions, bulk operations, location updates, week locks/unlocks</li>
            <li><strong>Shift Templates</strong> - Template creation, modifications, and deletions</li>
            <li><strong>PTO Requests</strong> - Submissions, approvals, denials, cancellations, and deletions</li>
            <li><strong>WFH Requests</strong> - Submissions, approvals, denials, cancellations, updates, and bulk business-directed WFH</li>
            <li><strong>Shift Swaps</strong> - Swap requests, acceptances, approvals, denials, and cancellations</li>
            <li><strong>Rotations</strong> - Rotation creation, editing, deletion, and application</li>
            <li><strong>Admin Configuration</strong> - Role permission changes, shift/WFH/PTO type configs, lunch break policies, staffing rules</li>
            <li><strong>Teams & Holidays</strong> - Team creation/updates, company holiday management, holiday volunteer sign-ups</li>
            <li><strong>Activities & Feedback</strong> - Activity logging, activity type management, feedback submissions</li>
            <li><strong>AI Actions</strong> - Shifts added or removed via AI Chat (marked with an AI badge)</li>
            <li><strong>Authentication</strong> - Login events, password changes</li>
          </ul>

          <h3 className="font-semibold text-lg mt-6">Using the Audit Log</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Navigate to <strong>Audit Log</strong> in the sidebar</li>
            <li>Click <strong>Show Filters</strong> to access advanced filtering</li>
            <li>Filter by:
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li><strong>Entity Type</strong> - Narrow to specific categories (schedule, PTO request, user, etc.)</li>
                <li><strong>Action</strong> - Filter by operation type (created, updated, deleted, approved, denied, etc.)</li>
                <li><strong>User</strong> - Search by user to see all actions they performed <em>or</em> were affected by</li>
                <li><strong>Date Range</strong> - View changes within a specific time period</li>
              </ul>
            </li>
            <li>Click any entry to expand it and see:
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li>The <strong>action</strong> performed (created, updated, deleted, approved, etc.)</li>
                <li><strong>Who</strong> performed it (resolved to the user's full name)</li>
                <li><strong>Before/After values</strong> showing exactly what changed</li>
                <li><strong>Metadata</strong> with additional context (dates, types, counts)</li>
                <li><strong>Timestamp</strong> of when it happened</li>
              </ul>
            </li>
          </ol>

          <h3 className="font-semibold text-lg mt-6">Searching by User</h3>
          <p className="text-gray-700">When you search the audit log by a specific user, the system finds <strong>all entries related to that person</strong> — including actions they performed, actions targeting them (e.g., a manager approving their PTO), and entries where they appear in swap requests. This gives you a complete picture of every interaction involving that employee.</p>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
            <p className="text-blue-700"><strong>Tip:</strong> PTO types, WFH types, and shift types are automatically resolved to their display names in the audit log. If a config type was deleted, its original ID may still appear.</p>
          </div>
        </div>
      )
    },
    {
      id: 'celebrations',
      title: 'Celebrations',
      icon: Cake,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Overview</h3>
          <p className="text-gray-700">The Celebrations feature highlights employee <strong>birthdays</strong> and <strong>work anniversaries</strong> right on the Dashboard, helping your team recognize and celebrate important milestones.</p>

          <h3 className="font-semibold text-lg mt-6">What's Displayed</h3>
          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
            <li><strong>Today's Celebrations</strong> - Prominently displayed cards for birthdays (with cake icon) and work anniversaries (with award icon showing milestone year)</li>
            <li><strong>Upcoming This Week</strong> - Compact pills showing birthdays and anniversaries in the next 7 days</li>
          </ul>

          <h3 className="font-semibold text-lg mt-6">Setting Up Employee Dates</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Go to <strong>Admin</strong> and find the employee</li>
            <li>Click <strong>Edit</strong> on the user</li>
            <li>Set the <strong>Birthday</strong> and/or <strong>Hire Date</strong> fields</li>
            <li>Click <strong>Save</strong></li>
          </ol>
          <p className="text-gray-700 mt-2">Only admins and managers can set these dates. Employees cannot edit their own birthday or hire date.</p>

          <h3 className="font-semibold text-lg mt-6">Enabling / Disabling</h3>
          <p className="text-gray-700">The Celebrations feature can be toggled on or off by an admin:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Go to <strong>Admin</strong> &gt; <strong>Settings</strong> tab</li>
            <li>Find the <strong>Feature Toggles</strong> card</li>
            <li>Toggle <strong>Dashboard Celebrations</strong> on or off</li>
          </ol>
          <p className="text-gray-700 mt-2">When disabled, the celebrations section will not appear on anyone's dashboard.</p>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4">
            <p className="text-amber-700"><strong>Note:</strong> Work anniversaries show the milestone year (e.g., "3 Year Anniversary!"). The anniversary is calculated from the hire date. Employees hired in the current year won't show an anniversary until the following year.</p>
          </div>
        </div>
      )
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: AlertCircle,
      content: (
        <div className="space-y-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <h4 className="font-medium text-red-800">"No shifts found" when requesting WFH</h4>
            <ul className="list-disc list-inside mt-2 text-red-700 space-y-1">
              <li>You can only request WFH for dates where you have scheduled shifts</li>
              <li>Use <strong>Historical Entry</strong> mode for past dates without shifts</li>
            </ul>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
            <h4 className="font-medium text-amber-800">WFH request showing wrong day count</h4>
            <ul className="list-disc list-inside mt-2 text-amber-700 space-y-1">
              <li>Ensure you selected the correct hours (4 for half day, 8 for full day)</li>
              <li>The day count is calculated based on your selection</li>
            </ul>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
            <h4 className="font-medium text-amber-800">Can't submit WFH request</h4>
            <ul className="list-disc list-inside mt-2 text-amber-700 space-y-1">
              <li>Make sure you've selected a <strong>WFH Type</strong> (required field)</li>
              <li>Ensure you have sufficient Flex Bank balance (for Flex Bank type)</li>
              <li>Check that shifts are selected (unless using Historical mode)</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <h4 className="font-medium text-blue-800">Schedule not showing copied shifts</h4>
            <ul className="list-disc list-inside mt-2 text-blue-700 space-y-1">
              <li>Wait a moment for the page to refresh</li>
              <li>Navigate away and back to the Schedule page</li>
              <li>Check that the target week dates are correct</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'ai-copilot',
      title: 'AI Copilot',
      icon: MessageSquare,
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Overview</h3>
          <p className="text-gray-700">The AI Copilot assists managers with scheduling tasks through natural language chat and proactive insights. It uses your staffing rules, schedules, and employee data to provide intelligent recommendations.</p>

          <h3 className="font-semibold text-lg mt-6">AI Chat (Action-Capable)</h3>
          <p className="text-gray-700">The AI Chat can <strong>directly execute scheduling actions</strong> from natural language commands:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
            <li><strong>Add shifts</strong> - "Add a regular shift for Alex Smith on Monday from 9am-5pm"</li>
            <li><strong>Remove shifts</strong> - "Remove Alex's on-call shift on Friday"</li>
            <li><strong>Answer questions</strong> - "Who's scheduled for on-call this weekend?"</li>
            <li><strong>Analyze coverage</strong> - "Are we understaffed on Wednesday?"</li>
          </ul>
          <p className="text-gray-700 mt-2">Actions performed via AI Chat are logged in the Audit Log with an <strong>AI badge</strong> for full traceability.</p>

          <h3 className="font-semibold text-lg mt-6">Proactive Insights</h3>
          <p className="text-gray-700">AI-powered analysis of your schedule against staffing rules. This feature is <strong>opt-in</strong> — click <strong>Run Analysis</strong> to trigger it.</p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
            <li>Coverage gap detection compared to staffing rules</li>
            <li>Understaffing and overstaffing alerts by time block</li>
            <li>PTO impact analysis on upcoming coverage</li>
            <li>Results are cached for 5 minutes to optimize costs</li>
          </ul>

          <h3 className="font-semibold text-lg mt-6">AI Schedule Generation</h3>
          <p className="text-gray-700">Auto-generate schedules for a date range based on your staffing rules and employee availability. Access this from the Schedule page's <strong>Auto-Schedule</strong> button.</p>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
            <p className="text-blue-700"><strong>Cost Note:</strong> AI features use a tiered model system — lightweight tasks (chat, insights) use a faster/cheaper model, while complex tasks (schedule generation) use a more capable model. Universal Key balance is required for AI features.</p>
          </div>
        </div>
      )
    }
  ];

  // Helper to safely extract text from React elements for search
  const extractText = (element) => {
    if (!element) return '';
    if (typeof element === 'string') return element;
    if (Array.isArray(element)) return element.map(extractText).join(' ');
    if (element.props) {
      const { children } = element.props;
      return extractText(children);
    }
    return '';
  };

  const filteredSections = sections.filter(section => {
    if (searchTerm === '') return true;
    const term = searchTerm.toLowerCase();
    if (section.title.toLowerCase().includes(term)) return true;
    try {
      const text = extractText(section.content);
      return text.toLowerCase().includes(term);
    } catch {
      return false;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <HelpCircle className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Help Center</h1>
          </div>
          <p className="text-gray-600">Everything you need to know about using Shiftela</p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search help topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>
        </div>

        {/* Quick Reference Card */}
        <Card className="mb-6 bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0">
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold mb-4">Quick Reference</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/20 rounded-lg p-3">
                <h3 className="font-semibold mb-2">Request WFH</h3>
                <p className="text-sm text-blue-100">Schedule → Request WFH → Select Type → Submit</p>
              </div>
              <div className="bg-white/20 rounded-lg p-3">
                <h3 className="font-semibold mb-2">Copy Week</h3>
                <p className="text-sm text-blue-100">Schedule → Copy Week → Set Dates → Copy</p>
              </div>
              <div className="bg-white/20 rounded-lg p-3">
                <h3 className="font-semibold mb-2">Request PTO</h3>
                <p className="text-sm text-blue-100">PTO/WFH → Request PTO → Fill Details → Submit</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Accordion Sections */}
        <div className="space-y-3">
          {filteredSections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSection === section.id;
            
            return (
              <Card key={section.id} className="overflow-hidden">
                <button
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">{section.title}</span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {isExpanded && (
                  <CardContent className="border-t bg-white">
                    {section.content}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Report Bug / Request Enhancement Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              <span>Report Bug / Request Enhancement</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3 mb-4">
              <Button
                onClick={() => { setShowFeedbackForm(true); setShowMyFeedback(false); }}
                className={showFeedbackForm ? 'bg-blue-600' : 'bg-gray-600'}
              >
                <Send className="h-4 w-4 mr-2" />
                Submit Feedback
              </Button>
              <Button
                onClick={() => { setShowMyFeedback(true); setShowFeedbackForm(false); }}
                variant="outline"
                className={showMyFeedback ? 'border-blue-600 text-blue-600' : ''}
              >
                <Eye className="h-4 w-4 mr-2" />
                View My Submissions
              </Button>
            </div>

            {/* Feedback Form */}
            {showFeedbackForm && (
              <form onSubmit={handleSubmitFeedback} className="space-y-4 bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={feedbackForm.type}
                      onValueChange={(value) => { setFeedbackForm({...feedbackForm, type: value}); setFormErrors({...formErrors, type: undefined}); }}
                    >
                      <SelectTrigger className={`bg-white ${formErrors.type ? 'border-red-500 ring-1 ring-red-500' : ''}`} data-testid="feedback-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bug">
                          <div className="flex items-center">
                            <Bug className="h-4 w-4 mr-2 text-red-500" />
                            Bug Report
                          </div>
                        </SelectItem>
                        <SelectItem value="enhancement">
                          <div className="flex items-center">
                            <Lightbulb className="h-4 w-4 mr-2 text-purple-500" />
                            Enhancement Request
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {formErrors.type && <p className="text-red-500 text-xs mt-1" data-testid="feedback-type-error">{formErrors.type}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <Select
                      value={feedbackForm.priority}
                      onValueChange={(value) => setFeedbackForm({...feedbackForm, priority: value})}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={feedbackForm.title}
                    onChange={(e) => { setFeedbackForm({...feedbackForm, title: e.target.value}); setFormErrors({...formErrors, title: undefined}); }}
                    placeholder="Brief description of the issue or request"
                    className={`bg-white ${formErrors.title ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                    data-testid="feedback-title-input"
                  />
                  {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={feedbackForm.description}
                    onChange={(e) => { setFeedbackForm({...feedbackForm, description: e.target.value}); setFormErrors({...formErrors, description: undefined}); }}
                    placeholder="Please provide as much detail as possible. For bugs: steps to reproduce, expected behavior, actual behavior. For enhancements: what you'd like to see and why."
                    className={`bg-white min-h-[120px] ${formErrors.description ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                    data-testid="feedback-description-input"
                  />
                  {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
                </div>

                {/* Screenshot Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Image className="h-4 w-4 inline mr-1" />
                    Screenshot (optional)
                  </label>
                  {!screenshotPreview ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleScreenshotChange}
                        className="hidden"
                        id="screenshot-upload"
                      />
                      <label htmlFor="screenshot-upload" className="cursor-pointer">
                        <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">Click to upload a screenshot</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                      </label>
                    </div>
                  ) : (
                    <div className="relative inline-block">
                      <img 
                        src={screenshotPreview} 
                        alt="Screenshot preview" 
                        className="max-h-40 rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={removeScreenshot}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* System Info Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <Monitor className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Automatic Debug Info</p>
                      <p className="text-xs text-blue-600">
                        To help us troubleshoot faster, we'll automatically include your browser type, 
                        screen size, and current page URL with your submission.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowFeedbackForm(false);
                      setScreenshot(null);
                      setScreenshotPreview(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </div>
              </form>
            )}

            {/* My Submissions List */}
            {showMyFeedback && (
              <div className="space-y-3">
                {myFeedback.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>You haven't submitted any feedback yet.</p>
                  </div>
                ) : (
                  myFeedback.map((item) => (
                    <div key={item.id} className="bg-gray-50 p-4 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            {getTypeBadge(item.type)}
                            {getStatusBadge(item.status)}
                            <Badge variant="outline" className="capitalize">{item.priority} priority</Badge>
                          </div>
                          <h4 className="font-medium text-gray-900">{item.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            Submitted: {new Date(item.created_at).toLocaleDateString()}
                          </p>
                          {item.admin_notes && (
                            <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                              <span className="font-medium text-blue-800">Admin Response:</span>
                              <p className="text-blue-700">{item.admin_notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Need more help? Contact your manager or system administrator.</p>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
